import {
    IAvailableSlot,
    IEirSalesViewAvailableAppointments,
} from "@modules/eirSalesFacade/interfaces/models/IEirSalesViewAvailableAppointments";

export interface IAvailableDate {
    date: Date;
    slots: string[];
}

export const slotCodeToLabel: Record<string, string[]> = {
    AM: ["Morning (8:30 to 13:30)"],
    PM: ["Afternoon (13:30 to 17:30)"],
    AD: ["All day (8:30 to 17:30)"],
};

/**
 * Parse et valide une date depuis une chaîne de caractères
 * @param dateStr - Chaîne de date au format YYYY-MM-DD
 * @returns Date parsée ou null si invalide
 */
const parseDate = (dateStr: string): Date | null => {
    if (!dateStr) {
        return null;
    }

    try {
        const parsedDate = new Date(dateStr);

        if (isNaN(parsedDate.getTime())) {
            return null;
        }

        return parsedDate;
    } catch {
        return null;
    }
};

/**
 * Convertit les codes de créneaux (AM, PM, AD) en labels complets avec heures
 * @param slotCodes - Tableau de codes de créneaux
 * @returns Tableau de labels uniques
 */
const convertSlotCodesToLabels = (slotCodes: string[]): string[] => {
    if (!slotCodes?.length) {
        return [];
    }

    const slotLabels = new Set(slotCodes.flatMap((slotCode) => slotCodeToLabel[slotCode] ?? [slotCode]));

    return Array.from(slotLabels);
};

/**
 * Mappe un slotData en IAvailableDate
 * @param slotData - Données de créneau à mapper
 * @returns IAvailableDate ou null si les données sont invalides
 */
const mapSlotDataToAvailableDate = (slotData: { at?: string; slots?: string[] }): IAvailableDate | null => {
    const { at: dateStr, slots: slotCodes } = slotData;

    if (!dateStr || !slotCodes?.length) {
        return null;
    }

    const parsedDate = parseDate(dateStr);
    if (!parsedDate) {
        return null;
    }

    const slotLabels = convertSlotCodesToLabels(slotCodes);

    return {
        date: parsedDate,
        slots: slotLabels,
    };
};

/**
 * Trie les dateList disponibles par ordre chronologique
 * @param dateList - Tableau de dateList disponibles
 * @returns Tableau de dateList triées
 */
const sortDatesByChronologicalOrder = (dateList: IAvailableDate[]): IAvailableDate[] => {
    return dateList.sort((a, b) => a.date.getTime() - b.date.getTime());
};

/**
 * Transforme les availableSlots en tableau de dates disponibles
 * @param availableSlots - Tableau de créneaux disponibles depuis l'API
 * @returns Tableau de dates disponibles triées
 */
const processAvailableSlots = (availableSlots: IAvailableSlot[]): IAvailableDate[] => {
    const dateList = availableSlots
        .map(mapSlotDataToAvailableDate)
        .filter((date): date is IAvailableDate => date !== null);

    return sortDatesByChronologicalOrder(dateList);
};

export const mapAvailableAppointmentList = (
    availableAppointments: IEirSalesViewAvailableAppointments | undefined
): IAvailableDate[] => {
    if (!availableAppointments?.availableSlots || availableAppointments.availableSlots.length === 0) {
        return [];
    }

    return processAvailableSlots(availableAppointments.availableSlots);
};
